import React, { useState, useEffect } from "react";
import produce from "immer";
import { v4 as uuid } from "uuid";
import {
  Wrapper,
  LabelButtonWrapper,
  Label,
  Buttons,
  Button,
  Table,
} from "./styledEdit";
import Cols from "./editComponents/Cols";
import Rows from "./editComponents/Rows";
import SettingsModal from "./editComponents/SettingsModal";
import SubLabel from "../common3/SubLabel";

const defaultCol = {};

const defaultRow = {};

const defaultCell = {
  expr: "",
  defaultValue: "",
};

const distributeRemainderBetweenCols = (remainderToDistribute, cols) => {
  const remainder = remainderToDistribute - cols.length;

  for (let i = 0; i < cols.length; i++) {
    const col = cols[i];
    col.width++;
  }

  if (remainder > 0 && remainder !== remainderToDistribute) {
    distributeRemainderBetweenCols(remainder, cols);
  }
};

const calculateWidth = (cols) => {
  const MAX_WIDTH = 100;
  const divisor = cols.length;
  const remainder = MAX_WIDTH % divisor;

  cols.forEach((col) => {
    col.width = Math.floor(MAX_WIDTH / divisor);
  });

  if (remainder > 0) {
    distributeRemainderBetweenCols(remainder, cols);
  }
};

const DatasheetEdit = (props) => {
  const { extra, id, save, runEffect, effects, setIsDndDisabled } = props;

  const { cols: extraCols, rows: extraRows, label, subLabel } = extra;

  const [cols, setCols] = useState(extraCols);
  const [rows, setRows] = useState(extraRows);
  const [settingsDraft, setSettingsDraft] = useState(null);

  useEffect(() => {
    runEffect({
      source: "Components/Datasheet/getValidations",
      effectId: id,
    });
  }, []);

  useEffect(() => {
    save({ _id: id, key: "cols", value: cols });
  }, [cols]);

  useEffect(() => {
    save({ _id: id, key: "rows", value: rows });
  }, [rows]);

  useEffect(() => {
    if (settingsDraft) {
      setIsDndDisabled(true);
    }
  }, [settingsDraft]);

  const addRow = (idx) => {
    setRows(
      produce((draft) => {
        if (idx === undefined) {
          draft.push({
            id: uuid(),
            ...defaultRow,
            cells: cols.map(() => ({ id: uuid(), ...defaultCell })),
          });
        } else {
          draft.splice(idx, 0, {
            id: uuid(),
            ...defaultRow,
            cells: cols.map(() => ({ id: uuid(), ...defaultCell })),
          });
        }
      })
    );
  };

  const removeRow = (idx) => {
    setRows(
      produce((draft) => {
        draft.splice(idx, 1);
      })
    );
  };

  const addCol = (idx) => {
    setCols(
      produce((draft) => {
        if (idx === undefined) {
          draft.push({
            id: uuid(),
            ...defaultCol,
          });
        } else {
          draft.splice(idx, 0, {
            id: uuid(),
            ...defaultCol,
          });
        }

        calculateWidth(draft);
      })
    );

    setRows(
      produce((draft) => {
        draft.forEach(({ cells }) => {
          if (idx === undefined) {
            cells.push({ id: uuid(), ...defaultCell });
          } else {
            cells.splice(idx, 0, { id: uuid(), ...defaultCell });
          }
        });
      })
    );
  };

  const removeCol = (idx) => {
    setCols(
      produce((draft) => {
        draft.splice(idx, 1);
        calculateWidth(draft);
      })
    );

    setRows(
      produce((draft) => {
        draft.forEach(({ cells }) => {
          cells.splice(idx, 1);
        });
      })
    );
  };

  const onChange = ({ type, rowIdx, cellIdx, key, value }) => {
    switch (type) {
      case "col":
        setCols(
          produce((draft) => {
            draft[cellIdx][key] = value;
          })
        );

        break;
      case "row":
        setRows(
          produce((draft) => {
            draft[rowIdx].cells[cellIdx][key] = value;
          })
        );

        break;
      default:
        return;
    }
  };

  const validations = effects[id] || [];

  return (
    <Wrapper>
      <LabelButtonWrapper>
        <Label>{label}</Label>
        <Buttons>
          <Button onClick={() => addRow()}>Add row</Button>
          <Button onClick={() => addCol()}>Add col</Button>
        </Buttons>
      </LabelButtonWrapper>
      {subLabel && <SubLabel>{subLabel}</SubLabel>}
      <Table>
        <Cols
          cols={cols}
          setSettingsDraft={setSettingsDraft}
          addCol={addCol}
          removeCol={removeCol}
        />
        <Rows
          rows={rows}
          cols={cols}
          setSettingsDraft={setSettingsDraft}
          addRow={addRow}
          removeRow={removeRow}
          onChange={onChange}
        />
      </Table>
      {settingsDraft && (
        <SettingsModal
          {...settingsDraft}
          setCols={setCols}
          setRows={setRows}
          setSettingsDraft={setSettingsDraft}
          validations={validations}
          setIsDndDisabled={setIsDndDisabled}
          cols={cols}
        />
      )}
    </Wrapper>
  );
};

export default DatasheetEdit;
